import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { buyerOrdersService } from '../../../services/buyerService';

const GeographicSourcingMap = ({ language, startDate, endDate }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  const content = {
    en: {
      title: 'Geographic Sourcing Patterns',
      country: 'Country',
      orders: 'Orders',
      spending: 'Total Spending',
      noData: 'No geographic data available'
    },
    zh: {
      title: '地理采购模式',
      country: '国家',
      orders: '订单',
      spending: '总支出',
      noData: '没有地理数据'
    }
  };

  const t = content?.[language];

  useEffect(() => {
    loadGeographicData();
  }, [startDate, endDate]);

  const loadGeographicData = async () => {
    try {
      setLoading(true);
      const orders = await buyerOrdersService?.getAll();

      // Filter by date range
      const filtered = orders?.filter(order => {
        const orderDate = new Date(order?.createdAt);
        return orderDate >= startDate && orderDate <= endDate;
      });

      // Group by country
      const grouped = filtered?.reduce((acc, order) => {
        const country = order?.bean?.country?.[`name${language === 'zh' ? 'Zh' : 'En'}`] || 'Unknown';
        if (!acc?.[country]) {
          acc[country] = { country, orders: 0, spending: 0 };
        }
        acc[country].orders += 1;
        acc[country].spending += (order?.quantityKg || 0) * (order?.bean?.priceUsdPerKg || 0);
        return acc;
      }, {});

      const chartData = Object.values(grouped)?.sort((a, b) => b?.spending - a?.spending);
      setData(chartData);
    } catch (error) {
      console.error('Failed to load geographic data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center justify-center h-64">
          <Icon name="Loader2" size={32} className="animate-spin text-primary" />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center gap-2 mb-6">
        <Icon name="Globe" size={20} className="text-primary" />
        <h3 className="text-lg font-heading font-semibold text-foreground">{t?.title}</h3>
      </div>
      {data?.length === 0 ? (
        <div className="text-center py-12">
          <Icon name="Map" size={48} className="text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground">{t?.noData}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {data?.map((item, index) => (
            <div key={index} className="flex items-center justify-between p-4 bg-muted/30 rounded-lg">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center text-primary font-semibold">
                  {index + 1}
                </div>
                <div>
                  <p className="font-medium text-foreground">{item?.country}</p>
                  <p className="text-sm text-muted-foreground">
                    {item?.orders} {t?.orders}
                  </p>
                </div>
              </div>
              <div className="text-right">
                <p className="font-semibold text-foreground">${item?.spending?.toFixed(2)}</p>
                <p className="text-xs text-muted-foreground">{t?.spending}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default GeographicSourcingMap;